/**
 * rainSource
 *
 * The rain ambience for the native shell.
 *
 * The web hook streams an mp3 from soundjay.com. Inside a shipping binary that is
 * three separate problems: a third-party host becomes a runtime dependency of an
 * offline-capable app, the domain shows up in the app's network profile for no
 * user benefit, and an `<audio>` element plays into the iOS "ambient" category,
 * where the hardware ring switch silences it and it fights the play-and-record
 * category the microphone needs. The same trap `audioUnlock.js` was written to
 * escape, re-entered by a decorative feature.
 *
 * So on native the rain is synthesised with WebAudio, in the AudioContext that
 * `audioUnlock` already owns. That means: no network, no asset, no ring-switch
 * silence, and one audio session rather than two fighting each other.
 *
 * Filtered noise is genuinely what rain is. Brown noise through a gentle band
 * shape, plus a slow drifting gain so it breathes instead of sitting flat, reads
 * as steady rain rather than as a hiss.
 *
 * If a licensed rain recording is ever bundled with the app, swap the buffer
 * build for a decode of that file. Nothing else here changes.
 */

import { ensureAudioContextRunning } from '@/pages/Timo/talk/audioUnlock.js';

// Long enough that the loop point is not audible as a repeating pattern.
const LOOP_SECONDS = 8;

/**
 * Brown noise: white noise integrated, which drops roughly 6 dB per octave and
 * gives the low, full "downpour" body. Pure white noise sounds like static.
 */
function buildRainBuffer(ctx) {
  const frames = Math.floor(ctx.sampleRate * LOOP_SECONDS);
  const buffer = ctx.createBuffer(1, frames, ctx.sampleRate);
  const data = buffer.getChannelData(0);

  let last = 0;
  for (let i = 0; i < frames; i += 1) {
    const white = Math.random() * 2 - 1;
    last = (last + 0.02 * white) / 1.02;
    data[i] = last * 3.5;
  }

  // Cross-fade the tail into the head so the loop seam is inaudible.
  const fade = Math.floor(ctx.sampleRate * 0.25);
  for (let i = 0; i < fade; i += 1) {
    const k = i / fade;
    data[i] = data[i] * k + data[frames - fade + i] * (1 - k);
  }

  return buffer;
}

/**
 * Start the rain. Resolves to null when WebAudio is unavailable, so callers can
 * simply fall through to silence.
 *
 * @param {{ volume?: number }} options volume is 0..1
 * @returns {Promise<{ setVolume(v: number): void, stop(): void } | null>}
 */
export async function startRain({ volume = 0.5 } = {}) {
  const ctx = await ensureAudioContextRunning();
  if (!ctx) return null;

  let stopped = false;

  try {
    const source = ctx.createBufferSource();
    source.buffer = buildRainBuffer(ctx);
    source.loop = true;

    // Band shape: cut the sub rumble, roll off the top so it is soft rather than
    // hissy, and leave a small resonant lift where rain-on-a-window lives.
    const highpass = ctx.createBiquadFilter();
    highpass.type = 'highpass';
    highpass.frequency.value = 180;

    const lowpass = ctx.createBiquadFilter();
    lowpass.type = 'lowpass';
    lowpass.frequency.value = 2200;

    const shelf = ctx.createBiquadFilter();
    shelf.type = 'peaking';
    shelf.frequency.value = 1200;
    shelf.Q.value = 0.7;
    shelf.gain.value = 3;

    const gain = ctx.createGain();
    gain.gain.value = 0;

    // Slow drift, so the rain swells and eases instead of sitting at one level.
    const lfo = ctx.createOscillator();
    lfo.type = 'sine';
    lfo.frequency.value = 0.06;
    const lfoGain = ctx.createGain();
    lfoGain.gain.value = 0.12;
    lfo.connect(lfoGain);
    lfoGain.connect(gain.gain);

    source.connect(highpass);
    highpass.connect(lowpass);
    lowpass.connect(shelf);
    shelf.connect(gain);
    gain.connect(ctx.destination);

    source.start(0);
    lfo.start(0);

    // Fade in: an ambience that snaps on at full level is startling, which is
    // exactly wrong for the users this feature exists for.
    const target = Math.max(0, Math.min(1, volume));
    try {
      gain.gain.setValueAtTime(0, ctx.currentTime);
      gain.gain.linearRampToValueAtTime(target, ctx.currentTime + 1.2);
    } catch {
      gain.gain.value = target;
    }

    return {
      setVolume(v) {
        if (stopped) return;
        const next = Math.max(0, Math.min(1, v));
        try {
          gain.gain.setTargetAtTime(next, ctx.currentTime, 0.2);
        } catch {
          gain.gain.value = next;
        }
      },
      stop() {
        if (stopped) return;
        stopped = true;
        try {
          gain.gain.setTargetAtTime(0, ctx.currentTime, 0.25);
        } catch {
          /* non-fatal */
        }
        // Let the fade finish before tearing the graph down.
        setTimeout(() => {
          try { source.stop(); } catch { /* already stopped */ }
          try { lfo.stop(); } catch { /* already stopped */ }
          try { source.disconnect(); } catch {}
          try { highpass.disconnect(); } catch {}
          try { lowpass.disconnect(); } catch {}
          try { shelf.disconnect(); } catch {}
          try { lfoGain.disconnect(); } catch {}
          try { gain.disconnect(); } catch {}
        }, 600);
      },
    };
  } catch {
    return null;
  }
}

export default startRain;
